import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface PdfPreviewDialogData {
  /** The already-fetched PDF blob. */
  blob: Blob;
  /** Filename used when the user clicks Download. */
  fileName: string;
  /** Optional dialog title. */
  title?: string;
}

/**
 * Previews a PDF in a modal (inline browser viewer) with a Download button —
 * so the user can look at the agreement before saving it. The caller fetches
 * the blob (showing its own spinner) and hands it in.
 */
@Component({
  selector: 'app-pdf-preview-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './pdf-preview-dialog.component.html',
  styleUrls: ['./pdf-preview-dialog.component.scss']
})
export class PdfPreviewDialogComponent implements OnDestroy {
  readonly title: string;
  private readonly objectUrl: string;
  readonly safeUrl: SafeResourceUrl;

  constructor(
    public dialogRef: MatDialogRef<PdfPreviewDialogComponent>,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: PdfPreviewDialogData
  ) {
    this.title = data.title ?? 'Premium Finance Agreement';
    this.objectUrl = URL.createObjectURL(data.blob);
    // Blob URLs are same-origin and app-created — safe to trust for the viewer iframe.
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
  }

  download(): void {
    const a = document.createElement('a');
    a.href = this.objectUrl;
    a.download = this.data.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    URL.revokeObjectURL(this.objectUrl);
  }
}
